"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { BundledLanguage } from "shiki";
import {
  Artifact,
  ArtifactActions,
  ArtifactClose,
  ArtifactContent,
  ArtifactHeader,
  ArtifactTitle,
} from "@/components/ai-elements/artifact";
import { CodeBlockContent } from "@/components/ai-elements/code-block";
import {
  MARKDOWN_DOCUMENT_PREVIEW_CLASS_NAME,
  MarkdownPreviewContent,
  MarkdownPreviewToggle,
  MarkdownPreviewToolbar,
} from "@/components/markdown-preview";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { CopyIcon } from "@/components/ui/copy";
import { Maximize2Icon } from "@/components/ui/maximize-2";
import { BoxesIcon } from "@/components/ui/boxes";
import { DownloadIcon } from "@/components/ui/download";
import { FolderCheckIcon } from "@/components/ui/folder-check";
import { FolderPlusIcon } from "@/components/ui/folder-plus";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { dataUrlToBytes } from "@/lib/data-url";
import { extOf, LANGUAGE_BY_EXT } from "@/lib/file-kinds";
import { downloadFile } from "@/lib/download-file";
import { supabase } from "@/lib/supabase";
import {
  CheckIcon,
  Code2Icon,
  ExternalLinkIcon,
  EyeIcon,
  RefreshCwIcon,
} from "lucide-react";

const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "svg", "webp"]);
// "plaintext" is a shiki SpecialLanguage, not a BundledLanguage — CodeBlockContent
// only accepts BundledLanguage, so CodeFileRenderer renders it as a plain <pre>
// instead of routing it through shiki.
type CodeLanguage = BundledLanguage | "plaintext";

// Extension -> language comes from the shared registry (@/lib/file-kinds) so
// this panel, the sandbox files panel and the attachment router can't drift
// apart again. Only the entries that need this panel's "plaintext" special
// language, which is not a BundledLanguage, are declared here.
const CODE_FILE_LANGUAGES: Partial<Record<string, CodeLanguage>> = {
  ...LANGUAGE_BY_EXT,
  txt: "plaintext",
};

const getCodeLanguage = (filename: string, mediaType?: string): CodeLanguage | null => {
  const mapped = CODE_FILE_LANGUAGES[extOf(filename)];
  if (mapped) return mapped;
  if (mediaType?.startsWith("text/")) return "plaintext";
  return null;
};

// Fetch PDFs before embedding: the viewer uses an app-owned blob URL rather
// than navigating its iframe to a cross-origin storage endpoint.
const usePdfFrame = (url: string, enabled: boolean, retryKey: number) => {
  const [result, setResult] = useState<{
    url: string;
    retryKey: number;
    src: string | null;
    error: boolean;
  } | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let blobUrl: string | null = null;
    void (async () => {
      try {
        let bytes: Uint8Array<ArrayBuffer> | ArrayBuffer;
        if (url.startsWith("data:")) {
          bytes = dataUrlToBytes(url);
        } else {
          const headers: Record<string, string> = {};
          if (url.startsWith("/api/")) {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
          }
          const response = await fetch(url, { headers, signal: controller.signal });
          if (!response.ok) throw new Error(`PDF request failed: ${response.status}`);
          bytes = await response.arrayBuffer();
        }
        if (controller.signal.aborted) return;
        blobUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        setResult({ url, retryKey, src: blobUrl, error: false });
      } catch {
        if (!controller.signal.aborted) {
          setResult({ url, retryKey, src: null, error: true });
        }
      }
    })();
    return () => {
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url, enabled, retryKey]);

  return enabled && result?.url === url && result.retryKey === retryKey ? result : null;
};

// Converts office documents to PDF via LibreOffice WASM and renders in an <iframe>
const DocxRenderer = ({
  url,
  filename,
  retryKey,
}: {
  url: string;
  filename: string;
  retryKey: number;
}) => {
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ percent: number; message: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;
    const converterCleanup = async () => {
      const { destroyLibreOfficeConverter } = await import("@/lib/libreoffice-loader");
      await destroyLibreOfficeConverter();
    };

    // Reset state synchronously so the old PDF doesn't linger during retry
    setStatus("loading");
    setPdfUrl(null);
    setProgress(null);

    // 3-minute timeout — if WASM init or conversion hangs, surface an error
    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        void converterCleanup();
        setStatus("error");
      }
    }, 3 * 60 * 1000);

    (async () => {
      try {
        // Fetch source document
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch source document: ${response.status}`);
        }

        const buffer = await response.arrayBuffer();
        if (cancelled) return;

        // Always prefer the actual selected filename (includes extension).
        // URL paths can be extensionless (e.g. /api/files/{id}/content).
        const rawUrlFilename = url.split("/").pop() ?? "document.docx";
        const fallbackFilename = rawUrlFilename.split("?")[0];
        const inputFilename = filename.trim() || fallbackFilename;

        const { getLibreOfficeConverter, destroyLibreOfficeConverter } = await import("@/lib/libreoffice-loader");
        if (cancelled) return;

        // Always start from a clean worker so a stuck spreadsheet/presentation cannot poison the next preview.
        await destroyLibreOfficeConverter();
        if (cancelled) return;

        const converter = await getLibreOfficeConverter((info) => {
          if (!cancelled) setProgress({ percent: info.percent, message: info.message });
        });
        if (cancelled) return;

        setProgress({ percent: 100, message: "Converting…" });

        const result = await converter.convert(
          new Uint8Array(buffer),
          { outputFormat: "pdf" },
          inputFilename
        );
        if (cancelled) return;

        blobUrl = URL.createObjectURL(
          new Blob([result.data.slice(0)], { type: "application/pdf" })
        );
        setPdfUrl(blobUrl);
        setProgress(null);
        setStatus("ready");
      } catch (err) {
        console.error("[DocxRenderer]", err);
        void converterCleanup();
        if (!cancelled) setStatus("error");
      } finally {
        clearTimeout(timeoutId);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      void converterCleanup();
    };
  }, [url, filename, retryKey]);

  return (
    <div className="relative size-full">
      {status === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background">
          <div className="size-5 animate-spin rounded-full border-2 border-border border-t-foreground" />
          {progress && (
            <div className="flex w-64 flex-col items-center gap-1">
              <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
              <p className="text-center text-xs text-muted-foreground">{progress.message}</p>
            </div>
          )}
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
          <p className="text-destructive text-sm">Failed to render document</p>
          <p className="text-muted-foreground text-xs">Use the refresh button in the toolbar to retry</p>
        </div>
      )}
      {pdfUrl && (
        <iframe
          src={pdfUrl}
          className="size-full border-0"
          title="Document preview"
        />
      )}
    </div>
  );
};

const MarkdownRenderer = ({ url, viewMode = "preview" }: { url: string; viewMode?: "preview" | "raw" }) => {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    setError(false);
    (async () => {
      try {
        // data: URLs are in-memory — decode directly, no fetch needed (and CSP would block it)
        if (url.startsWith("data:")) {
          const commaIdx = url.indexOf(",");
          if (commaIdx === -1) throw new Error("Invalid data URL");
          const decoded = decodeURIComponent(url.slice(commaIdx + 1));
          if (!cancelled) setContent(decoded);
          return;
        }
        const headers: Record<string, string> = {};
        if (url.startsWith("/api/")) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
        }
        const r = await fetch(url, { headers });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const text = await r.text();
        if (!cancelled) setContent(text);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (error) return <p className="p-4 text-sm text-destructive">Failed to load file.</p>;
  if (content === null) return <div className="flex size-full items-center justify-center"><div className="size-5 animate-spin rounded-full border-2 border-border border-t-foreground" /></div>;

  return (
    <div className="size-full overflow-auto overscroll-contain scrollbar-minimal">
      <MarkdownPreviewContent
        mode={viewMode === "raw" ? "markdown" : "preview"}
        value={content}
        readOnly
        markdownCodeView
        codeClassName="overflow-visible pt-16"
        previewClassName={MARKDOWN_DOCUMENT_PREVIEW_CLASS_NAME}
        emptyPreviewText="This Markdown file is empty."
      />
    </div>
  );
};

export const DocPreviewPanel = ({
  filename,
  url,
  mediaType,
  width,
  onClose,
  onSaveToWidgets,
  onAddToProject,
}: {
  filename: string;
  url: string;
  mediaType?: string;
  width: number;
  onClose: () => void;
  onSaveToWidgets?: () => Promise<void>;
  /** Attaches this file to the current project and indexes it for RAG retrieval. */
  onAddToProject?: () => Promise<{ ok: boolean; error?: string }>;
}) => {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  const codeLanguage = getCodeLanguage(filename, mediaType);
  const isPdf = ext === "pdf";
  const isImage = IMAGE_EXTS.has(ext);
  const isMarkdown = ext === "md" || ext === "mdx";
  const isHtml = ext === "html" || ext === "htm" || mediaType === "text/html";
  const isCodeFile = codeLanguage !== null && !isMarkdown && !isHtml;
  const isDocx = ext === "docx" || ext === "doc";
  const isPptx = ext === "pptx" || ext === "ppt";
  const isXlsx = ext === "xlsx" || ext === "xls";
  const needsConversion = isDocx || isPptx || isXlsx;
  const canToggleView = isHtml || isMarkdown;
  const canCopySource = canToggleView || isCodeFile;

  const [retryKey, setRetryKey] = useState(0);
  const pdfFrame = usePdfFrame(url, isPdf, retryKey);
  const [isSavingWidget, setIsSavingWidget] = useState(false);
  const [addToProjectState, setAddToProjectState] = useState<"idle" | "pending" | "added">("idle");
  const [viewMode, setViewMode] = useState<"preview" | "source">("preview");
  const [fetchedContent, setFetchedContent] = useState<string | null>(null);
  const [htmlFrame, setHtmlFrame] = useState<{ url: string; src: string | null; error: boolean } | null>(null);
  const htmlPreview = isHtml && htmlFrame?.url === url ? htmlFrame : null;
  const [copied, setCopied] = useState(false);
  const [markdownExpanded, setMarkdownExpanded] = useState(false);

  const urlRef = useRef(url);
  useEffect(() => {
    urlRef.current = url;
    setViewMode("preview");
    setMarkdownExpanded(false);
    setFetchedContent(null);
    setAddToProjectState("idle");
  }, [url]);

  // HTML bytes stay in the browser: one fetch supplies preview, source and
  // download. Navigating to a provider/storage URL can be blocked by its frame
  // policy or trigger a download; an app-owned blob matches text widgets.
  useEffect(() => {
    if (!canCopySource) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    if (isHtml) setHtmlFrame({ url, src: null, error: false });
    void (async () => {
      try {
        let bytes: Uint8Array<ArrayBuffer> | ArrayBuffer;
        if (url.startsWith("data:")) {
          bytes = dataUrlToBytes(url);
        } else {
          const headers: Record<string, string> = {};
          if (url.startsWith("/api/")) {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
          }
          if (controller.signal.aborted) return;
          const res = await fetch(url, { headers, signal: controller.signal });
          if (!res.ok) throw new Error(`Preview request failed: ${res.status}`);
          bytes = await res.arrayBuffer();
        }
        if (controller.signal.aborted) return;
        setFetchedContent(new TextDecoder().decode(bytes));
        if (isHtml) {
          objectUrl = URL.createObjectURL(new Blob([bytes], { type: "text/html" }));
          setHtmlFrame({ url, src: objectUrl, error: false });
        }
      } catch {
        if (!controller.signal.aborted && isHtml) setHtmlFrame({ url, src: null, error: true });
      }
    })();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, canCopySource, isHtml, retryKey]);

  const handleCopy = useCallback(async () => {
    if (!fetchedContent) return;
    await navigator.clipboard.writeText(fetchedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [fetchedContent]);

  // The passed-in handler already reports success/failure via toast (it's
  // shared with the file's row in the chat) — just track the icon here.
  // Guard against the panel showing a different file (or being closed) by
  // the time this resolves — the same panel instance is reused across
  // previews, so a stale response must not overwrite the current file's icon.
  const handleAddToProject = useCallback(async () => {
    if (!onAddToProject) return;
    const requestUrl = url;
    setAddToProjectState("pending");
    const result = await onAddToProject();
    if (requestUrl !== urlRef.current) return;
    setAddToProjectState(result.ok ? "added" : "idle");
  }, [onAddToProject, url]);

  return (
    <Artifact className="m-2 rounded-xl border shadow-sm" aria-label={isMarkdown ? filename : undefined} style={{ width, minWidth: isMarkdown ? 320 : 288, flexShrink: 0 }}>
      {!isMarkdown && <ArtifactHeader>
        <ArtifactTitle className="min-w-0 flex-1 truncate">
          {filename}
        </ArtifactTitle>
        <ArtifactActions>
          {(needsConversion || isPdf || isHtml) && (
            <button
              aria-label="Refresh preview"
              className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              onClick={() => setRetryKey((k) => k + 1)}
            >
              <RefreshCwIcon size={16} />
            </button>
          )}
          {canToggleView && !isMarkdown && (
            <>
              <button
                aria-label="Preview"
                aria-pressed={viewMode === "preview"}
                className={`flex size-8 items-center justify-center rounded-md transition-colors hover:bg-accent hover:text-foreground ${viewMode === "preview" ? "bg-accent text-foreground" : "text-muted-foreground"}`}
                onClick={() => setViewMode("preview")}
              >
                <EyeIcon size={16} />
              </button>
              <button
                aria-label="Source"
                aria-pressed={viewMode === "source"}
                className={`flex size-8 items-center justify-center rounded-md transition-colors hover:bg-accent hover:text-foreground ${viewMode === "source" ? "bg-accent text-foreground" : "text-muted-foreground"}`}
                onClick={() => setViewMode("source")}
              >
                <Code2Icon size={16} />
              </button>
            </>
          )}
          {canCopySource && !isMarkdown && (
            <button
              aria-label={copied ? "Copied!" : "Copy source"}
              className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
              disabled={!fetchedContent}
              onClick={handleCopy}
            >
              {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
            </button>
          )}
          {isHtml && onSaveToWidgets && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-label="Save to Widgets"
                    className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
                    disabled={isSavingWidget}
                    onClick={async () => {
                      setIsSavingWidget(true);
                      try {
                        await onSaveToWidgets();
                      } finally {
                        setIsSavingWidget(false);
                      }
                    }}
                  >
                    {isSavingWidget ? (
                      <svg className="size-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" /></svg>
                    ) : (
                      <BoxesIcon size={16} />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>Save to Widgets</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {onAddToProject && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-label={addToProjectState === "added" ? "Added to project" : "Add to project"}
                    className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
                    disabled={addToProjectState !== "idle"}
                    onClick={() => void handleAddToProject()}
                  >
                    {addToProjectState === "pending" ? (
                      <svg className="size-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" /></svg>
                    ) : addToProjectState === "added" ? (
                      <FolderCheckIcon size={16} />
                    ) : (
                      <FolderPlusIcon size={16} />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {addToProjectState === "added" ? "Added to project" : "Add to project"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <a
            aria-label="Open in new tab"
            className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            href={url}
            rel="noreferrer"
            target="_blank"
          >
            <ExternalLinkIcon size={16} />
          </a>
          {!isMarkdown && <button
            aria-label="Download"
            className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            onClick={() => void downloadFile(htmlPreview?.src ?? url, filename)}
            type="button"
          >
            <DownloadIcon size={16} />
          </button>}
          <ArtifactClose onClick={onClose} />
        </ArtifactActions>
      </ArtifactHeader>}
      <ArtifactContent className={isMarkdown ? "relative min-h-0 overflow-hidden p-0" : "relative p-0"}>
        {isMarkdown && (
          <MarkdownPreviewToolbar>
            <MarkdownPreviewToggle
              mode={viewMode === "source" ? "markdown" : "preview"}
              onModeChange={(mode) => setViewMode(mode === "markdown" ? "source" : "preview")}
              hideSplit
              className="h-8"
            />
            <Button type="button" size="icon" variant="ghost" className="size-8 p-0 text-muted-foreground hover:text-foreground" aria-label={copied ? "Copied!" : "Copy"} title={copied ? "Copied!" : "Copy"} disabled={fetchedContent === null} onClick={() => void handleCopy()}>
              {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
            </Button>
            <Button type="button" size="icon" variant="ghost" className="size-8 p-0 text-muted-foreground hover:text-foreground" aria-label="Download Markdown" title="Download Markdown" onClick={() => void downloadFile(url, filename)}>
              <DownloadIcon size={16} />
            </Button>
            <Button type="button" size="icon" variant="ghost" className="size-8 p-0 text-muted-foreground hover:text-foreground" aria-label="Expand Markdown preview" title="Expand Markdown preview" onClick={() => setMarkdownExpanded(true)}>
              <Maximize2Icon size={16} />
            </Button>
            {onAddToProject && (
              <Button type="button" size="icon" variant="ghost" className="size-8 p-0 text-muted-foreground hover:text-foreground" aria-label={addToProjectState === "added" ? "Added to project" : "Add to project"} title={addToProjectState === "added" ? "Added to project" : "Add to project"} disabled={addToProjectState !== "idle"} onClick={() => void handleAddToProject()}>
                {addToProjectState === "added" ? <FolderCheckIcon size={16} /> : <FolderPlusIcon size={16} />}
              </Button>
            )}
            <Button asChild size="icon" variant="ghost" className="size-8 p-0 text-muted-foreground hover:text-foreground">
              <a aria-label="Open in new tab" title="Open in new tab" href={url} rel="noreferrer" target="_blank">
                <ExternalLinkIcon size={16} />
              </a>
            </Button>
            <ArtifactClose onClick={onClose} title="Close preview" />
          </MarkdownPreviewToolbar>
        )}
        <Dialog open={markdownExpanded} onOpenChange={setMarkdownExpanded}>
          <DialogContent className="flex h-[90dvh] max-w-5xl! flex-col gap-0 overflow-hidden p-0">
            <DialogHeader className="shrink-0 border-b px-6 py-4 pr-12">
              <DialogTitle>{filename}</DialogTitle>
            </DialogHeader>
            <div className="shrink-0 px-6 pt-4">
              <MarkdownPreviewToggle mode={viewMode === "source" ? "markdown" : "preview"} onModeChange={(mode) => setViewMode(mode === "markdown" ? "source" : "preview")} hideSplit />
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <MarkdownRenderer url={url} viewMode={viewMode === "source" ? "raw" : "preview"} />
            </div>
          </DialogContent>
        </Dialog>
        {isPdf ? (
          pdfFrame?.src ? (
            <iframe className="size-full border-none" src={pdfFrame.src} title={filename} />
          ) : pdfFrame?.error ? (
            <p className="p-4 text-sm text-destructive">Failed to load PDF. Try refreshing the preview or downloading the file.</p>
          ) : (
            <p className="p-4 text-sm text-muted-foreground">Loading PDF…</p>
          )
        ) : isImage ? (
          <div className="flex size-full items-center justify-center p-4">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img alt={filename} className="max-h-full max-w-full object-contain" src={url} />
          </div>
        ) : isMarkdown ? (
          <MarkdownRenderer url={url} viewMode={viewMode === "source" ? "raw" : "preview"} />
        ) : isHtml ? (
          viewMode === "source" ? (
            <div className="size-full overflow-auto">
              <CodeBlockContent code={fetchedContent ?? ""} language="html" showLineNumbers />
            </div>
          ) : htmlPreview?.error ? (
            <p className="p-4 text-sm text-destructive">Failed to load HTML. Try refreshing the preview or downloading the file.</p>
          ) : htmlPreview?.src ? (
            <iframe
              className="size-full border-none bg-background"
              // Browser-owned bytes use the same sandbox as text widgets.
              // React's current iframe typings omit this boolean attribute.
              {...{ credentialless: "" }}
              referrerPolicy="no-referrer"
              sandbox="allow-forms allow-modals allow-popups allow-scripts"
              src={htmlPreview.src}
              title={filename}
            />
          ) : <Spinner className="m-4" />
        ) : isCodeFile ? (
          <CodeFileRenderer language={codeLanguage} url={url} />
        ) : needsConversion ? (
          <DocxRenderer filename={filename} url={url} retryKey={retryKey} />
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
            <p className="font-medium text-sm">{filename}</p>
            <p className="text-muted-foreground text-xs">Preview not available</p>
            <Button
              onClick={() => void downloadFile(url, filename)}
              size="sm"
              variant="outline"
            >
              <DownloadIcon size={14} />
              Download
            </Button>
          </div>
        )}
      </ArtifactContent>
    </Artifact>
  );
};

const CodeFileRenderer = ({ language, url }: { language: CodeLanguage; url: string }) => {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    setError(false);

    (async () => {
      try {
        if (url.startsWith("data:")) {
          const commaIdx = url.indexOf(",");
          if (commaIdx === -1) throw new Error("Invalid data URL");
          if (!cancelled) setContent(decodeURIComponent(url.slice(commaIdx + 1)));
          return;
        }

        const headers: Record<string, string> = {};
        if (url.startsWith("/api/")) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
        }

        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const text = await res.text();
        if (!cancelled) setContent(text);
      } catch {
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) return <p className="p-4 text-sm text-destructive">Failed to load file.</p>;
  if (content === null) return <div className="flex size-full items-center justify-center"><div className="size-5 animate-spin rounded-full border-2 border-border border-t-foreground" /></div>;

  if (language === "plaintext") {
    return (
      <pre className="size-full overflow-auto p-4 font-mono text-sm whitespace-pre-wrap text-foreground">
        {content}
      </pre>
    );
  }

  return (
    <div className="size-full overflow-auto">
      <CodeBlockContent code={content} language={language} showLineNumbers />
    </div>
  );
};
